import { Entry, Prisma, TaxIncentiveStatuses } from "@prisma/client";

// Temporarily declare the CreditPercentage type locally until global types are resolved
type CreditPercentage = {
  name: string;
  value: string;
}[];

export type ProjectWithRelations = Prisma.ProjectGetPayload<{
  include: { costAccounts: true; employees: true; incentiveRules: true };
}>;

export type EntryWithRelations = Partial<Entry>;

export type EntryItem = {
  id: string;
  src: string;
  amount: string;
  accountDescription: string;
  description: string;
  vendorName: string;
  laborLine: string;
  aboveTheLine: string;
  taxIncentiveStatus: TaxIncentiveStatuses;
};

// related properties are included with dot notation for nested properties
export enum Properties {
  SRC = "src",
  TRANSACTION_NUMBER = "transactionNumber",
  AMOUNT = "amount",
  ACCOUNT_CODE = "accountCode",
  ACCOUNT_DESCRIPTION = "accountDescription",
  DESCRIPTION = "description",
  VENDOR_NAME = "vendorName",
  LABOR_LINE = "laborLine",
  ABOVE_THE_LINE = "aboveTheLine",
  TAX_INCENTIVE_STATUS = "taxIncentiveStatus",
  DATE_OF_EXPENDITURE = "dateOfExpenditure",
  FF1 = "ff1",
  FF2 = "ff2",
  LEDGER_FF1 = "ledgerFf1",
  LEDGER_FF2 = "ledgerFf2",
  LOCATION = "location",
  EMPLOYEE_LAST_NAME = "employee.lastName",
  EMPLOYEE_JOB_TITLE = "employee.jobTitle",
  EMPLOYEE_STATE_OF_RESIDENCY = "employee.stateOfResidency",
  EMPLOYEE_ETHNICITY = "employee.ethnicity",
  EMPLOYEE_GENDER = "employee.gender",
  EMPLOYEE_CORP_NAME = "employee.corpName",
  EMPLOYEE_LAST_FOUR_SSN = "employee.lastFourSSN",
  EMPLOYEE_LAST_FOUR_EIN = "employee.lastFourEIN",
  EMPLOYEE_TYPE = "employee.type",
  EMPLOYEE_ZIP = "employee.zipCode",
  VENDOR_ADDRESS_LINE_ONE = "vendor.addressLineOne",
  VENDOR_ADDRESS_LINE_TWO = "vendor.addressLineTwo",
  VENDOR_CITY = "vendor.city",
  VENDOR_STATE = "vendor.state",
  VENDOR_ZIP = "vendor.zipCode",
  VENDOR_PHONE = "vendor.phone",
  VENDOR_EMAIL = "vendor.email",
  VENDOR_WEBSITE = "vendor.website",
  // Derived at evaluation time: whether the vendor has every doc type in the
  // program's requiredVendorDocs list. Not a real entry/vendor field.
  VENDOR_HAS_REQUIRED_DOCS = "vendor.hasRequiredDocs",
}

// Properties that resolve to a true/false value rather than free text
export const BOOLEAN_PROPERTIES: Properties[] = [
  Properties.VENDOR_HAS_REQUIRED_DOCS,
];

export enum UpdatableProperties {
  TAX_INCENTIVE_STATUS = "taxIncentiveStatus",
  FF1 = "ff1",
  FF2 = "ff2",
}

export type EntryItemWithReasons = EntryItem & {
  qualifyingReasons: string[];
  certainty: number;
};

export enum Modifiers {
  OR = "or",
  AND = "and",
}

export enum Operators {
  INCLUDES = "includes",
  EQUALS = "equals",
  IS_BEFORE = "is before",
  IS_AFTER = "is after",
}

export enum Conditions {
  OR = "or",
  AND = "and",
}

export type QualifierObj = {
  id: string;
  propertyName: Properties;
  operator: Operators;
  modifier: Conditions;
  values: Array<string | number | boolean>;
  isTemplate?: boolean;
  name?: string;
  additionalQualifiers?: QualifierObj[];
};

export type UpdateObj = {
  id: string;
  numberOfQualifiers: number;
  updates: {
    fieldName: UpdatableProperties;
    value: string;
  }[];
};

export type QualifierAction = {
  id: string;
  description: string;
  action: (entry: EntryItem, data?: unknown) => EntryItem;
};

export type Ruleset = {
  id: string;
  name: string;
  templateName?: string;
  qualifiers: QualifierObj[];
  actions: UpdateObj[];
  percentDeductionAllowed: number;
  maximumDeductionAllowed: number;
};

export type ProjectRule = {
  percentDeductionAllowed: CreditPercentage;
  maximumDeductionAllowed: number;
  minimumSpend: number;
};

export type IncentiveProgram = {
  id: string;
  name: string;
  locality: {
    country: string;
    region: string | null;
  };
  percentDeductionAllowed: CreditPercentage;
  maximumDeductionAllowed: number;
  minimumSpend: number;
  taxIncentiveCodes: string[];
  rules: Ruleset[];
  ledgerLocationMappings?: string[];
  // Docs this locality requires vendors to have on file, snapshotted from the
  // IncentiveRulesTemplate when the template is applied to the project.
  requiredVendorDocs?: string[];
};
